/**
 * tarif-section-forms — the tarif form body, shared by the full-page create surface (`TarifForm`)
 * and the edit modal (`TarifEditModal`), like `pobocka-section-forms`. Renders
 * `<form id={TARIF_FORM_ID}>` with fields only; the chrome supplies the submit button through the
 * HTML `form` attribute, so Enter and the browser's own URL check work.
 */

import type { FormEvent } from 'react';
import { Field, Fields } from '@/components/Field';
import { TextInput } from '@/components/TextInput';
import { useSeededSlice, type BodyProps } from '@/modules/vozidla/vozidlo-section-forms';
import type { PujcovnaTarif, TarifWriteBody } from './api';
import { CenikFields } from './CenikFields';
import { cenikSeed, cenikToBody, prazdnyCenik, type CenikValue } from './cenik-helpers';

export const TARIF_FORM_ID = 'pujcovna-tarif-form';

export interface TarifValue {
  nazev: string;
  vop_url: string;
  cenik: CenikValue;
}

export function tarifSeed(t?: PujcovnaTarif | null): TarifValue {
  return { nazev: t?.nazev ?? '', vop_url: t?.vop_url ?? '', cenik: t ? cenikSeed(t) : prazdnyCenik() };
}

export type TarifBodyVysledek = { ok: true; body: TarifWriteBody & { nazev: string } } | { ok: false; chyba: string };

/**
 * Trimmed; the list order (`poradi`) is not edited here — a new tarif goes to the end. With `original` (an edit) the pricing travels
 * SLICE-ONLY: a part is sent only when it differs from the row the form was opened on, so saving a new name never writes back a stale
 * markup, bands or seasons that someone else changed meanwhile. A pricing value that is not a number is named.
 */
export function tarifToBody(v: TarifValue, original?: PujcovnaTarif | null): TarifBodyVysledek {
  const c = cenikToBody(v.cenik);
  if (!c.ok) return c;
  const base = { nazev: v.nazev.trim(), vop_url: v.vop_url.trim() };
  if (!original) return { ok: true, body: { ...base, ...c.body } };
  const was = cenikToBody(cenikSeed(original));
  const prev = was.ok ? was.body : null;
  const body: TarifWriteBody & { nazev: string } = { ...base };
  if (JSON.stringify(c.body.marze_proc) !== JSON.stringify(prev?.marze_proc)) body.marze_proc = c.body.marze_proc;
  if (JSON.stringify(c.body.delky) !== JSON.stringify(prev?.delky)) body.delky = c.body.delky;
  if (JSON.stringify(c.body.sezony) !== JSON.stringify(prev?.sezony)) body.sezony = c.body.sezony;
  return { ok: true, body };
}

export function TarifFormBody({ initial, busy, onSubmit, onChange }: BodyProps<TarifValue>) {
  const { form, set } = useSeededSlice(initial, onChange);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit?.(form);
  };

  return (
    <form id={TARIF_FORM_ID} onSubmit={submit}>
      <Fields cols={2}>
        <Field label="Název tarifu" required>
          <TextInput
            value={form.nazev}
            maxLength={100}
            placeholder="Osobní malé"
            onChange={(e) => set('nazev', e.currentTarget.value)}
          />
        </Field>
        <Field
          label="Odkaz na VOP"
          optional="volitelné"
          hint="Adresa dokumentu s obchodními podmínkami, začíná http:// nebo https://."
        >
          <TextInput
            type="url"
            value={form.vop_url}
            maxLength={500}
            placeholder="https://"
            // Chromium keeps surrounding whitespace in the value, accepts it when validating a URL, but
            // refuses a value that is ONLY whitespace (type mismatch), which would block the submit of a
            // field the admin meant to clear. Collapse that case to '' as they type; padded URLs are
            // valid as they are and tarifToBody() trims them.
            onChange={(e) => {
              const v = e.currentTarget.value;
              set('vop_url', v.trim() === '' ? '' : v);
            }}
          />
        </Field>
      </Fields>
      <CenikFields value={form.cenik} busy={busy} onChange={(cenik) => set('cenik', cenik)} />
    </form>
  );
}
